import {
  LOCALE_ID,
  Inject,
  Component,
  OnInit,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
  FormArray,
} from '@angular/forms';
import { Order, LogElement } from '@models/order';
import { VendorOrder } from '@models/vendor-order';
import { OrderService } from '@services/order.service';
import { ProductService } from '@services/product.service';
import { StoreService } from '@services/store.service';
import { NotificationService } from '@services/notification.service';
import { AuthService } from '@services/auth.service';
import { VendorOrderService } from '@services/vendor-order.service';
import { Observable, of, map, combineLatest } from 'rxjs';
import { concatMap, takeWhile } from 'rxjs/operators';
import { OrdersBaseComponent } from '@components/admin/managed-board/base/orders-base/orders-base.component';
import { ImageEditComponent } from '@components/admin/image/image-edit/image-edit.component';
import { OkCancelComponent } from '@components/admin/dialog/ok-cancel/ok-cancel.component';
import { MatDialog } from '@angular/material/dialog';
import { OrderEditDialogComponent } from '@components/admin/patient/order-edit-dialog/order-edit-dialog.component';
import { InvoiceScanDialogComponent } from '@components/admin/managed-board/managed-board-awaiting-frames/invoice-scan-dialog/invoice-scan-dialog.component';
import { VendorInvoiceScanService } from '@services/vendor-invoice-scan.service';

@Component({
  selector: 'app-awaiting-frames-orders',
  templateUrl: './awaiting-frames-orders.component.html',
  styleUrls: ['./awaiting-frames-orders.component.css'],
})
export class AwaitingFramesOrdersComponent
  extends OrdersBaseComponent
  implements OnInit
{
  @Output() refreshOrders = new EventEmitter<boolean>();
  displayedColumns = [
    'select',
    'image',
    'model',
    'color',
    'size',
    'type',
    'cost',
    'retail',
    'patientStock',
    'patient',
    'invoiceNumber',
    'eta',
    'store',
    'view',
    'action',
    'log',
  ];
  authorizedRoles: string[] = ['MANAGER', 'OWNER/ADMIN', 'OPTICIAN/INVENTORY'];
  multipleSaveInitials: FormControl = new FormControl('', Validators.required);
  multipleSaveAndUpdateInitials: FormControl = new FormControl(
    '',
    Validators.required
  );
  multipleCancelInitials: FormControl = new FormControl(
    '',
    Validators.required
  );
  invoiceScanBusy = false;
  pendingInvoiceAttachment: any = null;
  allowDuplicateInvoice = false;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public auth: AuthService,
    public orderService: OrderService,
    public vendorOrderService: VendorOrderService,
    public productService: ProductService,
    public storeService: StoreService,
    public cdRef: ChangeDetectorRef,
    public fb: FormBuilder,
    public route: ActivatedRoute,
    public dialog: MatDialog,
    public notificationService: NotificationService,
    private vendorInvoiceScanService: VendorInvoiceScanService
  ) {
    super(
      locale,
      auth,
      orderService,
      vendorOrderService,
      productService,
      storeService,
      cdRef,
      fb,
      route,
      notificationService
    );
  }

  ngOnInit() {
    super.ngOnInit();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.orderForm.addControl(
      'invoiceNumber',
      new FormControl('', Validators.required)
    );
    this.orderForm.addControl('date', new FormControl('', Validators.required));
    this.orderForm.addControl('tax', new FormControl('', Validators.required));
    this.orderForm.addControl(
      'shipping',
      new FormControl('', Validators.required)
    );
    this.orderForm.addControl(
      'total',
      new FormControl('', Validators.required)
    );
    this.orderForm.addControl(
      'initials',
      new FormControl('', Validators.required)
    );

    // this.orderForm.statusChanges.subscribe((val) => {console.log(val);console.log(this.orderForm)})
    // this.orderForm.valueChanges.subscribe((val) => {console.log(val)})

    this.setOptionalOrderFormProperties(
      this.orderForm.get('orders') as FormArray
    );
    this.cdRef.detectChanges();
  }

  setOptionalOrderFormProperties(ordersFormArray: FormArray) {
    for (let x = 0; x < ordersFormArray.length; x++) {
      ordersFormArray.get(`${x}.initials`).clearValidators();
      ordersFormArray.get(`${x}.initials`).updateValueAndValidity();

      ordersFormArray
        .get(`${x}.products.0.product.vendor`)
        .setValidators([Validators.required]);
      ordersFormArray
        .get(`${x}.products.0.product.model`)
        .setValidators([Validators.required]);
      ordersFormArray
        .get(`${x}.products.0.product.color`)
        .setValidators([Validators.required]);
      ordersFormArray
        .get(`${x}.products.0.product.size`)
        .setValidators([Validators.required]);

      ordersFormArray
        .get(`${x}.products.0.cost`)
        .setValidators([Validators.required, Validators.min(0)]);
      ordersFormArray.get(`${x}.products.0.cost`).updateValueAndValidity();
      ordersFormArray
        .get(`${x}.products.0.retail`)
        .setValidators([Validators.required, Validators.min(0)]);
      ordersFormArray.get(`${x}.products.0.retail`).updateValueAndValidity();

      if (ordersFormArray.get(`${x}.products.0.cost`).invalid) {
        ordersFormArray.get(`${x}.products.0.cost`).markAsTouched();
      }
      if (ordersFormArray.get(`${x}.products.0.retail`).invalid) {
        ordersFormArray.get(`${x}.products.0.retail`).markAsTouched();
      }
    }

    this.cdRef.detectChanges();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
    this.calculateTotal();
  }

  toggleSelect(order) {
    this.selection.toggle(order);
    this.calculateTotal();
  }

  saveOrderFrame(order: FormGroup, newOrder: boolean = false) {
    super.saveFrame(order, newOrder);
    this.calculateTotal();
  }

  checkboxLabel(row?: FormGroup, index?): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
      index + 1
    }`;
  }

  clickOrder($event, order: FormGroup, index) {
    if (!this.fieldOpen) {
      if (this.orderIndex && this.index) {
        order.controls.notify.disable();
      }
      order.controls.notify.enable();
      this.orderId = order.value._id;
      this.index = index;
      this.fieldOpen = true;

      this.cdRef.detectChanges();

      //this.editProduct = product;
      //this.setProductForms(orderIndex);
      // $event.preventDefault();
      // $event.stopPropagation();
      //this.fieldOpen = false;
    }
  }

  openOrderEdit($event: Event, order: FormGroup): void {
    $event.preventDefault();
    $event.stopPropagation();
    const patientId =
      order.value?.patient?._id || order.value?.patient || null;
    const orderId = order.value?._id;
    if (!patientId || !orderId) {
      return;
    }

    const dialogRef = this.dialog.open(OrderEditDialogComponent, {
      width: '96vw',
      maxWidth: '96vw',
      height: '92vh',
      maxHeight: '92vh',
      autoFocus: false,
      panelClass: 'order-edit-dialog-panel',
      data: { patientId, orderId },
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.refreshOrders.emit();
      }
    });
  }

  onInvoiceFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files[0];
    if (input) {
      input.value = '';
    }
    if (!file) {
      return;
    }
    this.scanVendorInvoice(file);
  }

  scanVendorInvoice(file: File): void {
    const vendorId =
      this.vendorOrders?.vendor?._id || this.vendorOrders?.vendor || null;
    if (!vendorId) {
      this.notificationService.show(
        'error',
        'Open a vendor before scanning an invoice.',
        'Close'
      );
      return;
    }

    const awaitingOrders = (this.dataSource?.data || []).map((row: FormGroup) =>
      row.getRawValue()
    );
    this.invoiceScanBusy = true;
    this.vendorInvoiceScanService
      .scanInvoice(file, String(vendorId), awaitingOrders)
      .subscribe({
        next: (proposal) => {
          this.invoiceScanBusy = false;
          const objectUrl = URL.createObjectURL(file);
          const dialogRef = this.dialog.open(InvoiceScanDialogComponent, {
            width: '96vw',
            maxWidth: '1200px',
            maxHeight: '92vh',
            autoFocus: false,
            disableClose: true,
            data: {
              proposal,
              vendorName: this.vendorOrders?.vendor?.name,
              objectUrl,
            },
          });
          dialogRef.afterClosed().subscribe((result) => {
            URL.revokeObjectURL(objectUrl);
            if (result?.confirmed) {
              this.applyInvoiceScanConfirmation(result);
            }
          });
        },
        error: () => {
          this.invoiceScanBusy = false;
          this.notificationService.show(
            'error',
            'Unable to read vendor invoice.',
            'Close'
          );
        },
      });
  }

  private applyInvoiceScanConfirmation(result: any): void {
    this.pendingInvoiceAttachment = result.invoiceAttachment || null;
    this.allowDuplicateInvoice = !!result.proposal?.duplicateInvoice;
    this.orderForm.get('invoiceNumber')?.setValue(result.invoiceNumber);
    const dateValue = result.date ? new Date(result.date) : null;
    this.orderForm
      .get('date')
      ?.setValue(dateValue && !isNaN(dateValue.getTime()) ? dateValue : result.date);
    this.orderForm
      .get('shipping')
      ?.setValue(
        typeof result.shipping === 'number'
          ? result.shipping.toFixed(2)
          : result.shipping
      );
    this.orderForm
      .get('tax')
      ?.setValue(
        typeof result.tax === 'number' ? result.tax.toFixed(2) : result.tax
      );
    this.orderForm.get('initials')?.setValue(result.initials);

    this.selection.clear();
    const selectedIds = new Set(
      (result.selectedOrderIds || []).map((id: string) => String(id))
    );
    (this.dataSource?.data || []).forEach((row: FormGroup) => {
      if (selectedIds.has(String(row.value?._id))) {
        this.selection.select(row);
        const unitPrice = (result.proposal?.match?.matchedLines || []).find(
          (line) =>
            String(line?.match?.orderId) === String(row.value?._id) &&
            line?.line?.unitPrice != null
        )?.line?.unitPrice;
        if (unitPrice != null && row.get('products.0.cost')) {
          row.get('products.0.cost').setValue(unitPrice);
          row.get('products.0.cost').markAsDirty();
        }
      }
    });
    this.calculateTotal();
    if (result.total != null && this.orderForm.get('total')) {
      this.orderForm.get('total').setValue(result.total);
    }
    this.cdRef.detectChanges();

    if (!this.selection.selected.length) {
      this.notificationService.show(
        'error',
        'No matching awaiting frames were selected to receive.',
        'Close'
      );
      return;
    }

    this.saveIncomingOrder(true);
  }

  saveIncomingOrder(incomingBoolean) {
    // let vendorOrders: VendorOrders = this.dataSource.data[orderIndex];

    this.fieldOpen = true;

    let logItem = new LogElement();
    logItem.initials = this.orderForm.value.initials;
    logItem.user = this.auth.currentUser._id;
    logItem.notes = '';
    logItem.date = Date.now().toString();

    //this.addCurrentActionToLog(order, 'UPDATED');

    let vendorOrder = new VendorOrder();
    vendorOrder.date = this.orderForm.value.date;
    vendorOrder.type = 'order';
    vendorOrder.invoiceNumber = String(
      this.orderForm.value.invoiceNumber ?? ''
    ).trim();
    vendorOrder.tax = parseFloat(this.orderForm.value.tax.replace(/,/g, ''));
    vendorOrder.shipping = parseFloat(
      this.orderForm.value.shipping.replace(/,/g, '')
    );
    //vendorOrder.total = parseFloat(this.orderForm.value.total.replace(/,/g, ''));
    vendorOrder.total = parseFloat(this.orderForm.value.total);
    vendorOrder.vendor = this.vendorOrders.vendor;
    vendorOrder.user = this.auth.currentUser._id;
    vendorOrder.store = this.auth.getStore();
    vendorOrder.log.unshift(logItem);
    if (this.pendingInvoiceAttachment) {
      (vendorOrder as any).invoiceAttachment = this.pendingInvoiceAttachment;
    }

    this.ensureUniqueInvoiceNumber(vendorOrder).subscribe((isUnique) => {
      if (!isUnique && !this.allowDuplicateInvoice) {
        this.fieldOpen = false;
        return;
      }
      this.allowDuplicateInvoice = false;

      let totalTaxableCost = 0;
      let totalCost = 0;
      let totalTax = 0;
      let totalShipping = 0;
      let dirtyOrderCount = 0;
      for (let x = 0; x < this.selection.selected.length; x++) {
        let order = this.selection.selected[x].getRawValue();
        if (this.selection.selected[x].dirty) {
          dirtyOrderCount = dirtyOrderCount + 1;
          this.addDirtyValuesToLog(this.selection.selected[x]);
        } else {
          order.vendorOrder = vendorOrder._id;
          this.orderService
            .updateOrderVendorOrder(order)
            .subscribe((order2) => {});
        }
        totalCost = totalCost + order.products[0].cost;
        if (
          this.vendorOrders.vendor.taxBothRxAndSun ||
          order.products[0].type == 'RX'
        ) {
          totalTaxableCost = totalTaxableCost + order.products[0].cost;
        }
      }

      this.saveDirtyOrders(dirtyOrderCount).subscribe((saveDirtyOrders) => {
        for (let x = 0; x < this.selection.selected.length; x++) {
          // let order = JSON.parse(JSON.stringify(this.selection.selected[x]));
          let order = this.selection.selected[x].getRawValue();
          let dirty = this.selection.selected[x].dirty;

          order.status = 'RECEIVED';
          if (order.products?.[0]?.frame) {
            order.products[0].frame.dateReceived =
              order.products[0].frame.dateReceived || Date.now().toString();
          }
          this.appendReceivedActivity(order);
          this.applyOnApprovalReceived(order);
          logItem.item = order.part ? 'PART RECEIVED' : 'RECEIVED';
          order.log.unshift(logItem);
          order.products[0].store = order.products[0].store?._id
            ? order.products[0].store._id
            : order.products[0].store;

          if (
            this.vendorOrders.vendor.taxBothRxAndSun ||
            order.products[0].type == 'RX'
          ) {
            order.products[0].tax =
              Math.round(
                (100 * vendorOrder.tax * order.products[0].cost) /
                  totalTaxableCost
              ) / 100;
            totalTax = totalTax + order.products[0].tax;
          }

          order.products[0].shipping =
            Math.round(
              (100 * vendorOrder.shipping) / this.selection.selected.length
            ) / 100;
          totalShipping = totalShipping + order.products[0].shipping;

          if (x == this.selection.selected.length - 1) {
            if (totalShipping != vendorOrder.shipping) {
              let shippingDiff =
                Math.round(100 * (vendorOrder.shipping - totalShipping)) / 100;
              order.products[0].shipping =
                order.products[0].shipping + shippingDiff;
            }
            // LAST PRODUCT MIGHT NOT HAVE TAX... WE NEED TO REDO THIS

            if (totalTax != vendorOrder.tax) {
              let taxDiff =
                Math.round(100 * (vendorOrder.tax - totalTax)) / 100;
              order.products[0].tax = order.products[0].tax + taxDiff;
            }
          }
          order.products[0].product.cost = order.products[0].cost;
          order.products[0].product.retail = order.products[0].retail;
          order.products[0].product.pricingFormula =
            order.products[0].pricingFormula;
          order.products[0].product.type = order.products[0].type;
          order.vendorOrder = vendorOrder._id;
          vendorOrder.products.unshift(order.products[0]);
          vendorOrder.orders.unshift(order._id);
          vendorOrder.status = 'RECEIVED';
          vendorOrder.dateReceived = Date.now().toString();

          console.log(order)
          if (order.specialOrder) {
            order.products[0].product.specialOrder = true;
          }
          console.log('check dupliate 1')

          this.completeOrderReceive(order, vendorOrder, x, dirty, saveDirtyOrders);
        }
      });
    });
  }

  private appendReceivedActivity(order: any): void {
    const frame = order?.products?.[0]?.frame;
    if (!frame) {
      return;
    }
    if (!Array.isArray(frame.activity)) {
      frame.activity = [];
    }
    if (frame.activity.some((entry) => entry?.status === 'RECEIVED')) {
      return;
    }
    frame.activity.push({
      date: frame.dateReceived || new Date(),
      status: 'RECEIVED',
      referenceNumber: '',
    });
    frame.activityStatus = 'RECEIVED';
  }

  private applyOnApprovalReceived(order: any): void {
    const frame = order?.products?.[0]?.frame;
    const isOnApproval =
      frame?.physicalStatus === 'ON-APPROVAL' ||
      frame?.frameSource === 'PATIENT APPROVAL' ||
      order?.todo === 'ORDERED ON APPROVAL';
    if (!isOnApproval || !frame) {
      return;
    }

    const receivedRaw = frame.dateReceived ? new Date(frame.dateReceived) : new Date();
    const received = isNaN(receivedRaw.getTime()) ? new Date() : receivedRaw;
    frame.dateReceived = received;
    const followUp = new Date(received);
    followUp.setDate(followUp.getDate() + 5);
    frame.followUpDate = followUp;
    order.todo = 'ORDER';
    order.status =
      order.type === 'F'
        ? 'ORDER PART'
        : order.type === 'A'
          ? 'ORDER ACCESSORY'
          : 'ORDER FRAME';
  }

  private completeOrderReceive(
    order: any,
    vendorOrder: VendorOrder,
    index: number,
    dirty: boolean,
    saveDirtyOrders: boolean
  ): void {
    const saveTax$ =
      dirty && saveDirtyOrders
        ? this.orderService.updateOrderFull(order)
        : this.orderService.updateOrderTaxAndShipping(order);

    const processInventory$ =
      order.type != 'F' && !order.part
        ? this.productService
            .incrementProductInventoryQuantity(order.products[0].product, 1)
            .pipe(
              concatMap((incrementProduct) => {
                if (!order.products[0].product._id) {
                  order.products[0].product._id = incrementProduct.product._id;
                }
                if (
                  order.products[0].product._id != incrementProduct.product._id
                ) {
                  order.products[0].product = incrementProduct.product;
                  return this.orderService
                    .updateOrderFull(order)
                    .pipe(map(() => order));
                }
                return of(order);
              })
            )
        : of(order);

    saveTax$
      .pipe(
        concatMap(() => processInventory$),
        concatMap(() => this.orderService.updateOrderStatusAndLog(order))
      )
      .subscribe(() => {
        this.finalizeVendorOrderForOrder(order, vendorOrder, index);
      });
  }

  private finalizeVendorOrderForOrder(order: any, vendorOrder: VendorOrder, index: number) {
    if (index !== this.selection.selected.length - 1) {
      return;
    }
    this.vendorOrderService.addVendorOrder(vendorOrder).subscribe({
      next: (savedVendorOrder) => {
        this.productService
          .addVendorOrderToProduct(
            order.products[0].product._id,
            savedVendorOrder._id
          )
          .subscribe((productUpdated) => {
            console.log(productUpdated);
          });

        this.pendingInvoiceAttachment = null;
        this.refreshOrders.emit(true);
      },
      error: (err) => {
        const code = err?.error?.code;
        const message =
          err?.error?.message || 'Unable to create vendor order.';
        this.notificationService.show('error', message, 'Close');
        if (code !== 'DUPLICATE_INVOICE') {
          // console.error(err);
        }
      },
    });
  }

  private ensureUniqueInvoiceNumber(vendorOrder: VendorOrder) {
    const vendor: any = vendorOrder.vendor;
    const vendorId = (vendor && vendor._id) || vendor;

    if (!vendorId || !vendorOrder.invoiceNumber) {
      return of(true);
    }

    return this.vendorOrderService
      .getVendorOrderByInvoiceNumber(
        vendorId,
        vendorOrder.invoiceNumber,
        this.auth.getStore()
      )
      .pipe(
        map((orders) => {
          const isUnique = !orders || orders.length === 0;
          if (!isUnique && !this.allowDuplicateInvoice) {
            this.notificationService.show(
              'error',
              'Invoice number is already in use for this vendor.',
              'Close'
            );
          }
          return isUnique;
        })
      );
  }

  saveDirtyOrders(dirtyOrderCount): Observable<boolean> {
    if (dirtyOrderCount > 0) {
      let dialogRef = this.dialog.open(OkCancelComponent, {
        width: '250px',
        data: { message: 'SAVE MODIFIED ORDERS?' },
      });
      return dialogRef.afterClosed().pipe(takeWhile((result) => !!result));
    } else {
      return of(true);
    }
  }

  imageEdit(image, product, imageIndex) {
    this.cropImage(image);
  }

  cropImage(imageId) {
    let dialogRef = this.dialog.open(ImageEditComponent, {
      width: '600px',
      height: '500px',
      data: { image: imageId, message: '' },
    });
    dialogRef.afterClosed().subscribe((result) => {});
    dialogRef.componentInstance.imageChange.subscribe((img) => {
      //image.date = img.date;
    });
  }

  updateImages(images, product) {
    let imageFormArray: FormArray = product.get('images') as FormArray;
    // if (productElement.value.product._id) {
    //   this.productService.addImage(productElement.value.product._id, imageId[x])
    //     .subscribe(product => {
    //       console.log("product image added " + product._id);
    //     });
    // }
    console.log(images);
    console.log(product);
    console.log(imageFormArray);
    if (imageFormArray.value) {
      // imageFormArray.push(new FormControl(images[0]));
    } else {
      imageFormArray.setValue(images);
    }

    if (product.value._id != 'NEW') {
      product.get('images').setValue(images);
      if (product.value._id) {
        this.productService
          .updateImages(product.value._id, images)
          .subscribe((res) => {});
      }
    }
  }

  saveFrame(order: FormGroup): Observable<any> {
    return super.saveFrame(order).pipe(
      map(() => {
        this.calculateTotal();
        this.cdRef.detectChanges();
        return true;
      })
    );
  }

  multipleSaveChanges() {
    for (let x = 0; x < this.selection.selected.length; x++) {
      this.selection.selected[x]
        .get('initials')
        .setValue(this.multipleSaveInitials.value);
      this.saveFrame(this.selection.selected[x]).subscribe(() => {
        this.notificationService.show(
          'success',
          'SELECTED ORDERS SUCCESSFULLY SAVED'
        );
      });
    }
  }

  multipleSaveAndUpdateInventory() {
    for (let x = 0; x < this.selection.selected.length; x++) {
      this.selection.selected[x]
        .get('initials')
        .setValue(this.multipleSaveAndUpdateInitials.value);
      this.saveAndUpdateInventory(this.selection.selected[x]).subscribe(() => {
        this.notificationService.show(
          'success',
          'SELECTED ORDERS AND INVENTORY SUCCESSFULLY SAVED'
        );
      });
    }
  }

  multipleCancel() {
    this.selection.selected.forEach((order) =>
      order.get('initials').setValue(this.multipleCancelInitials.value)
    );
    const cancelFrames = this.selection.selected.map((order) =>
      this.cancelFrame(order)
    );
    combineLatest(cancelFrames).subscribe(() => {
      this.selection.clear();
      this.notificationService.show(
        'success',
        'SELECTED ORDERS SUCCESSFULLY CANCELED'
      );
      this.refreshOrders.emit(true);
    });
  }
}
